// Shared with mju-aioe/packages/shared/src/schemas/projectRisks.ts — keep the two identical.
/**
 * Risks and issues, end to end: one register per project, kept at the
 * project's home like the rest of its work layer (./projectWork.ts) — the
 * organisation for a project registered with it, the hub for a shared one,
 * the desktop's private hub for one kept on this computer (the Personal space).
 *
 * A **risk** is something that may happen; an **issue** is something that
 * has; a **bug** is a fault in what was built (severity, how to reproduce
 * it, what it affects, the version it was found in). Each entry belongs either to the whole project (`rootId: ""`) or to
 * one of its repositories (`rootId`), so a repository's view shows its own
 * and the project's view shows everything, rolled up. An entry names what it
 * threatens — roadmap items and tasks, the project's or a repository's — and
 * those show it in turn ({@link entriesForRoadmapRow}, {@link entriesForTask}).
 *
 *   GET    /projects/v1/:id/risks?rootId=&kind=&state=   { entries[], people{} }   state: open (default) | closed | all;
 *                                                         rootId: one repository's ("" = the project's own); absent = all
 *   POST   /projects/v1/:id/risks                        a draft → 201 the entry
 *   PATCH  /projects/v1/:id/risks/:uid                   a part of an entry (null drops a field) → the entry;
 *                                                         If-Match "<version>" → 412 precondition_failed { current }
 *   DELETE /projects/v1/:id/risks/:uid                   204
 *
 * The server gives an entry its `uid`, its number (`RK-n` for a risk, `IS-n`
 * for an issue, `BG-n` for a bug, counted per project and never reused), its `version` (1,
 * then +1 per accepted change) and who raised and last changed it — a client
 * cannot sign for someone else; an agent acting for a person is named beside
 * them (`actor`). Every accepted write is one line in the project's activity
 * log ({@link riskActivity}). Contributors and above write; viewers read;
 * anyone else gets 404.
 *
 * A risk that happens is not edited into an issue: its status becomes
 * `occurred` and an issue is raised with `fromRisk` naming it, so the record
 * of what was foreseen stays.
 *
 * **ISO 31000 (all optional, additive).** A risk may carry an assessment on
 * the project's risk template — likelihood × impact per dimension (Overall,
 * Cost, Schedule…), `inherent` (before controls) and `residual` (with them),
 * each with the score, the band it falls in and the one rating word that band
 * rolls up to, as the writer derived them from the template in force. The
 * rating of an entry is then its residual's (else its inherent's), so every
 * roll-up below works whatever the template. Beside it: `controls` (each with
 * an owner and how effective it is), a `disposition` (treat, tolerate,
 * transfer, terminate), `confidentiality`, the date it was identified, when
 * it is next reviewed, tags, and `treatments` — the tasks given out to deal
 * with it and how each ended. The template, the appetite at each level and
 * the review cadence are the project's **risk settings**, kept at its home:
 *
 *   GET    /projects/v1/:id/risks/settings               the settings ({} when none were saved: the built-in ISO 31000 5×5 applies)
 *   PUT    /projects/v1/:id/risks/settings               replace them → the settings (with `version`, `updatedAt`, `updatedBy`);
 *                                                         If-Match "<version>" → 412 precondition_failed { current }
 *
 * A home without the settings route answers 404/405: clients use the
 * built-in template and say so. Later, an organisation may publish its own
 * templates (`GET /organisation/v1/risk-templates` → `{ templates: RiskTemplate[] }`);
 * a project's settings name one by `templateId`.
 *
 * Words for people: risk, issue, bug, likelihood, impact, rating, owner,
 * response (what is being done about it), raised by, threatens, controls,
 * appetite, review.
 *
 * AIOE 0.35.0 (D247, B-12): an entry's `owner` stays the person's id at the home or a name; a client may send `{ id?, name }` (kept as the id,
 * else the name) and every answer adds `ownerPerson: { id?, name }`. The overdue review's needs-you record goes to the owner when there is one
 * (the workbench files it with `to` the owner; a record that names people reaches only them), else to the project's owners.
 */
import { z } from "zod";
import { taskDelegateSchema } from "./projectLinks.js";
export const PROJECT_RISKS_SCHEMA_VERSION = 1;
const id = z.string().min(1).max(200);
const by = z.object({ principal: z.string().max(200).optional(), actor: z.string().max(200).optional() });
export const RISK_KINDS = ["risk", "issue", "bug"];
export const riskKindSchema = z.enum(RISK_KINDS);
export const RISK_LEVELS = ["low", "medium", "high"];
export const riskLevelSchema = z.enum(RISK_LEVELS);
/**
 * A risk's states, an issue's, then a bug's. `open` is every kind's first.
 * A risk's follow ISO 31000's lifecycle: open (identified) → analysed →
 * mitigating (being treated) → monitoring → closed, or accepted, escalated,
 * or occurred (it happened: an issue is raised from it).
 */
export const RISK_STATUSES = ["open", "analysed", "mitigating", "monitoring", "escalated", "accepted", "occurred", "closed"];
export const ISSUE_STATUSES = ["open", "in-progress", "resolved", "wont-fix"];
export const BUG_STATUSES = ["open", "in-progress", "fixed", "wont-fix", "duplicate"];
export const riskStatusSchema = z.enum(["open", "mitigating", "accepted", "occurred", "closed", "in-progress", "resolved", "wont-fix", "analysed", "monitoring", "escalated", "fixed", "duplicate"]);
export const RISK_RATINGS_ORDER = ["low", "medium", "high", "critical"];
export const riskRatingSchema = z.enum(RISK_RATINGS_ORDER);
// ── ISO 31000: assessment, controls, treatment (all optional) ──────────
/** By dimension id (`overall`, `cost`, `schedule`…): the impact level on the template's scale. */
const impactsSchema = z.record(z.string().min(1).max(40), z.number().int().min(1).max(10)).refine((v) => Object.keys(v).length <= 20, "at most 20 dimensions");
/**
 * One assessment on the template's matrix. `score`, `band` and `rating` are
 * derived by the writer from the template in force (likelihood × the overall
 * impact, the band whose range holds the score, and the rating word that
 * band rolls up to). `basis` on a residual: `controls` when it was worked out
 * from the inherent and the controls (and follows them), `assessed` when a
 * person or agent set it.
 */
export const riskAssessmentSchema = z.object({
    likelihood: z.number().int().min(1).max(10),
    impacts: impactsSchema,
    score: z.number().int().min(1).max(100).optional(),
    band: z.string().max(40).optional(),
    rating: riskRatingSchema.optional(),
    basis: z.enum(["assessed", "controls"]).optional(),
    /** The band the entry stood in before this assessment, when this one moved it (a report says up or down). */
    was: z.string().max(40).optional(),
    assessedAt: z.string().max(50).optional(),
    notes: z.string().max(2000).optional(),
});
export const RISK_CONTROL_TYPES = ["preventive", "detective", "corrective", "directive"];
export const RISK_CONTROL_EFFECTIVENESS = ["not-effective", "partly", "mostly", "fully"];
/** A control's state; absent means it is in place. */
export const RISK_CONTROL_STATUSES = ["planned", "in-place", "under-review", "retired"];
/** Something in place (or planned) that makes a risk less likely or less costly. */
export const riskControlSchema = z.object({
    /** The writer's, unique in the entry (`c1`, `c2`…). */
    id: z.string().min(1).max(40),
    title: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    owner: z.string().max(200).optional(),
    type: z.enum(RISK_CONTROL_TYPES).optional(),
    effectiveness: z.enum(RISK_CONTROL_EFFECTIVENESS).optional(),
    status: z.enum(RISK_CONTROL_STATUSES).optional(),
    evidence: z.string().max(500).optional(),
});
/** The four Ts: reduce it, live with it, hand it to someone else, or stop doing what causes it. */
export const RISK_DISPOSITIONS = ["treat", "tolerate", "transfer", "terminate"];
export const RISK_CONFIDENTIALITY = ["public", "internal", "confidential", "restricted"];
export const BUG_SEVERITIES = ["blocker", "critical", "major", "minor", "trivial"];
/** Where a bug was raised from: a failing pipeline run, a reviewer's gap, a test, an agent, a person. */
export const riskRaisedFromSchema = z.object({
    kind: z.enum(["ci", "review", "test", "agent", "person"]),
    /** What it names there: a run, a review, a test's name. */
    ref: z.string().max(300).optional(),
    url: z.string().max(500).optional(),
});
/** The team whose agent raised an entry (D239). */
export const riskRaisedByTeamSchema = z.object({ id: z.string().min(1).max(200), name: z.string().max(200).optional(), rootId: z.string().max(200).optional() });
/** A roadmap item an entry threatens: a repository's row, or the project's own (`rootId: ""`, an `R-n`). */
export const riskRoadmapLinkSchema = z.object({ rootId: z.string().max(200), id });
/** A task an entry threatens or came from: a repository task, or a project task (`rootId: ""`, board `project`). */
export const riskTaskLinkSchema = z.object({ rootId: z.string().max(200), board: taskDelegateSchema.shape.board, uid: taskDelegateSchema.shape.uid });
export const RISK_TREATMENT_STATES = ["assigned", "done", "stopped"];
/** A task given out to deal with an entry, and how it ended (its board closed it done, or it was stopped). */
export const riskTreatmentSchema = z.object({
    task: riskTaskLinkSchema,
    /** Its number on its board (`T-4`). */
    taskId: z.string().max(50).optional(),
    title: z.string().max(500).optional(),
    assignee: z.string().max(200).optional(),
    state: z.enum(RISK_TREATMENT_STATES),
    at: z.string().max(50),
    doneAt: z.string().max(50).optional(),
    /** What the closure said was done. */
    summary: z.string().max(2000).optional(),
});
const fields = {
    kind: riskKindSchema,
    /** The repository it belongs to; "" for the whole project. */
    rootId: z.string().max(200).default(""),
    /** The repository's name in words (AIOE 0.34.0), put on every answer; never stored. */
    rootName: z.string().max(200).optional(),
    title: z.string().min(1).max(200),
    description: z.string().max(4000).optional(),
    status: riskStatusSchema.default("open"),
    /** How likely a risk is. An issue has happened: it carries none. */
    likelihood: riskLevelSchema.optional(),
    /** How much it costs the project when it happens, or now that it has. */
    impact: riskLevelSchema.default("medium"),
    /**
     * Who looks after it: a person's id at the home, or a name (what the workbench sends). A client may also send the D247
     * owner shape `{ id?, name }`: the routes keep it as the id, else the name, and every answer carries the person as `ownerPerson`.
     */
    owner: z.string().max(200).optional(),
    /** What is being done: the mitigation for a risk, the fix or workaround for an issue. */
    response: z.string().max(4000).optional(),
    /** A date it should be dealt with by (ISO date or date-time). */
    due: z.string().max(50).optional(),
    roadmap: z.array(riskRoadmapLinkSchema).max(50).default([]),
    tasks: z.array(riskTaskLinkSchema).max(50).default([]),
    /** On an issue: the risk (by uid) that happened. */
    fromRisk: id.optional(),
    // ── ISO 31000 and bugs: all optional ──
    /** What it is about, in a word (security, architecture, delivery, quality…): who is best placed to deal with it. */
    category: z.string().max(60).optional(),
    /** The template its assessments are on; absent = the project's. */
    templateId: z.string().max(80).optional(),
    /** A risk: before any control. */
    inherent: riskAssessmentSchema.optional(),
    /** A risk: with its controls. The entry's rating is this one's (else the inherent's). */
    residual: riskAssessmentSchema.optional(),
    controls: z.array(riskControlSchema).max(30).optional(),
    disposition: z.enum(RISK_DISPOSITIONS).optional(),
    confidentiality: z.enum(RISK_CONFIDENTIALITY).optional(),
    /** When it was first seen (ISO date). */
    identifiedOn: z.string().max(50).optional(),
    /** When it is next looked at again (ISO date); past it, the owner is asked to. */
    reviewDue: z.string().max(50).optional(),
    /** When it was last looked at. */
    reviewedAt: z.string().max(50).optional(),
    tags: z.array(z.string().min(1).max(40)).max(20).optional(),
    treatments: z.array(riskTreatmentSchema).max(30).optional(),
    /** A bug: how bad it is. */
    severity: z.enum(BUG_SEVERITIES).optional(),
    /** A bug: the steps to reproduce it, what was expected and what happened. */
    repro: z.string().max(8000).optional(),
    /** A bug: the area or the files it affects. */
    affects: z.array(z.string().min(1).max(300)).max(50).optional(),
    /** A bug: the version it was found in. */
    foundIn: z.string().max(80).optional(),
    /** A bug: the pull request that fixes it (a URL or `#n`). */
    pullRequest: z.string().max(500).optional(),
    raisedFrom: riskRaisedFromSchema.optional(),
    /**
     * The team whose agent raised it (D239, C-10): its id, its name for people and the repository it works in. A
     * team member may annotate only what its own team raised; the Watchlist names the team when more than one has
     * raised entries. Absent: a person raised it, or it predates teams being recorded.
     */
    raisedByTeam: riskRaisedByTeamSchema.optional(),
};
/** What a client sends to raise one. */
export const riskDraftSchema = z.object(fields);
/** A change: any of the fields but the kind and the repository; `null` drops an optional one. */
export const riskPatchSchema = z
    .object({
    title: fields.title,
    description: fields.description.nullable(),
    status: riskStatusSchema,
    likelihood: riskLevelSchema.nullable(),
    impact: riskLevelSchema,
    owner: fields.owner.nullable(),
    response: fields.response.nullable(),
    due: fields.due.nullable(),
    roadmap: z.array(riskRoadmapLinkSchema).max(50),
    tasks: z.array(riskTaskLinkSchema).max(50),
    fromRisk: id.nullable(),
    category: fields.category.nullable(),
    templateId: fields.templateId.nullable(),
    inherent: riskAssessmentSchema.nullable(),
    residual: riskAssessmentSchema.nullable(),
    controls: z.array(riskControlSchema).max(30).nullable(),
    disposition: z.enum(RISK_DISPOSITIONS).nullable(),
    confidentiality: z.enum(RISK_CONFIDENTIALITY).nullable(),
    identifiedOn: fields.identifiedOn.nullable(),
    reviewDue: fields.reviewDue.nullable(),
    reviewedAt: fields.reviewedAt.nullable(),
    tags: z.array(z.string().min(1).max(40)).max(20).nullable(),
    treatments: z.array(riskTreatmentSchema).max(30).nullable(),
    severity: z.enum(BUG_SEVERITIES).nullable(),
    repro: fields.repro.nullable(),
    affects: z.array(z.string().min(1).max(300)).max(50).nullable(),
    foundIn: fields.foundIn.nullable(),
    pullRequest: fields.pullRequest.nullable(),
    raisedFrom: riskRaisedFromSchema.nullable(),
})
    .partial();
export const riskEntrySchema = z.looseObject({
    ...fields,
    uid: id,
    /** `RK-n`, `IS-n` or `BG-n`, the server's. */
    id,
    version: z.number().int().positive(),
    raisedAt: z.string().max(50),
    raisedBy: by.optional(),
    updatedAt: z.string().max(50),
    updatedBy: by.optional(),
    /** When it stopped being open, while it is not. */
    closedAt: z.string().max(50).optional(),
    /**
     * The owner as a person (AIOE 0.35.0, D247, B-12), put on every answer and never stored: `id` when `owner` is a person of
     * the organisation, always `name`. Absent when the entry has no owner.
     */
    ownerPerson: z.looseObject({ id: z.string().min(1).max(200).optional(), name: z.string().min(1).max(200) }).optional(),
});
// ── The project's risk settings: template, appetite, review cadence ────
/** One step of a scale: its level (1 = least), its name and what it means. */
export const riskScaleStepSchema = z.object({ level: z.number().int().min(1).max(10), label: z.string().min(1).max(60), description: z.string().max(300).optional() });
/** A dimension an impact is judged on (`overall` is always there), with its own scale's words when it has them. */
export const riskDimensionSchema = z.object({ id: z.string().min(1).max(40), label: z.string().min(1).max(60), scale: z.array(riskScaleStepSchema).max(10).optional() });
/** A band of scores (inclusive), its colour, and the rating word it rolls up to. */
export const riskBandSchema = z.object({ id: z.string().min(1).max(40), label: z.string().min(1).max(60), min: z.number().int().min(0).max(100), max: z.number().int().min(0).max(100), colour: z.string().max(20), rating: riskRatingSchema });
/** The most a level accepts, by band id. */
export const riskAppetiteSchema = z.object({ organisation: z.string().max(40).optional(), project: z.string().max(40).optional(), repository: z.string().max(40).optional() });
/** A risk template: the standard it follows, its scales, the dimensions, the bands and its default appetite. */
export const riskTemplateSchema = z.object({
    id: z.string().min(1).max(80),
    name: z.string().min(1).max(120),
    standard: z.object({ name: z.string().max(120), publisher: z.string().max(120).optional(), version: z.string().max(40).optional() }).optional(),
    description: z.string().max(1000).optional(),
    likelihood: z.array(riskScaleStepSchema).min(2).max(10),
    /** The overall impact scale. */
    impact: z.array(riskScaleStepSchema).min(2).max(10),
    dimensions: z.array(riskDimensionSchema).max(15).optional(),
    /** Lowest first. */
    bands: z.array(riskBandSchema).min(2).max(10),
    appetite: riskAppetiteSchema.optional(),
    /** How often an open risk is looked at again, by default. */
    reviewEveryDays: z.number().int().min(1).max(730).optional(),
});
/** What a client sends to `PUT …/risks/settings`. Absent fields: the template's defaults. */
export const riskSettingsDraftSchema = z.object({
    /** The template the project uses; absent = the built-in ISO 31000 5×5. */
    templateId: z.string().max(80).optional(),
    /** The project's own templates, beside the built-in ones (and, later, the organisation's). */
    templates: z.array(riskTemplateSchema).max(10).optional(),
    /** The most each level accepts (band ids); a repository's own in `repositories`. */
    appetite: riskAppetiteSchema.optional(),
    /** Per repository (by its id): another template, its own appetite. */
    repositories: z.record(z.string().min(1).max(200), z.object({ templateId: z.string().max(80).optional(), appetite: z.string().max(40).optional() })).optional(),
    reviewEveryDays: z.number().int().min(1).max(730).optional(),
});
export const riskSettingsSchema = riskSettingsDraftSchema.extend({
    version: z.number().int().nonnegative().optional(),
    updatedAt: z.string().max(50).optional(),
    updatedBy: by.optional(),
});
// ── What the fields mean ───────────────────────────────────────────────
/** The statuses an entry of this kind may take. */
export function statusesFor(kind) {
    return kind === "risk" ? RISK_STATUSES : kind === "bug" ? BUG_STATUSES : ISSUE_STATUSES;
}
const OPEN_STATUSES = new Set(["open", "mitigating", "in-progress", "analysed", "monitoring", "escalated"]);
/** Whether it still needs attention: a risk being analysed, treated, watched or escalated; an issue or a bug not yet settled. */
export function isOpenEntry(entry) {
    return OPEN_STATUSES.has(entry.status);
}
export const RISK_RATINGS = ["critical", "high", "medium", "low"];
const LEVEL_SCORE = { low: 1, medium: 2, high: 3 };
const SEVERITY_RATING = { blocker: "critical", critical: "critical", major: "high", minor: "medium", trivial: "low" };
/**
 * One word for how much an entry matters. A risk assessed on a template: its
 * residual's rating, else its inherent's. Otherwise likelihood × impact
 * (unknown likelihood counts as medium). An issue has happened, so its
 * impact alone decides, one step up: a high-impact issue is critical. A bug:
 * its severity, else its impact as an issue's.
 */
export function riskRating(entry) {
    if (entry.kind === "risk") {
        const assessed = entry.residual?.rating ?? entry.inherent?.rating;
        if (assessed)
            return assessed;
    }
    if (entry.kind === "bug" && entry.severity)
        return SEVERITY_RATING[entry.severity];
    const impact = LEVEL_SCORE[entry.impact];
    if (entry.kind !== "risk")
        return impact === 3 ? "critical" : impact === 2 ? "high" : "medium";
    const score = impact * LEVEL_SCORE[entry.likelihood ?? "medium"];
    return score >= 9 ? "critical" : score >= 6 ? "high" : score >= 3 ? "medium" : "low";
}
export const RISK_STATUS_TEXT = {
    open: "Open",
    mitigating: "Being mitigated",
    accepted: "Accepted",
    occurred: "Happened",
    closed: "Closed",
    "in-progress": "Being fixed",
    resolved: "Resolved",
    "wont-fix": "Will not fix",
    analysed: "Analysed",
    monitoring: "Being monitored",
    escalated: "Escalated",
    fixed: "Fixed",
    duplicate: "Duplicate",
};
export const RISK_RATING_TEXT = { critical: "Critical", high: "High", medium: "Medium", low: "Low" };
/** The next number for a kind, from every entry the project ever had (a removed one's number is not reused: pass the highest seen). */
export function nextRiskId(kind, highest) {
    return `${kind === "risk" ? "RK" : kind === "bug" ? "BG" : "IS"}-${highest + 1}`;
}
/** The number in an `RK-n` / `IS-n` / `BG-n`, or 0. */
export function riskIdNumber(entryId) {
    const m = /^(?:RK|IS|BG)-(\d+)$/.exec(entryId);
    return m ? Number(m[1]) : 0;
}
/** Why a draft or a change cannot be accepted, in words; undefined when it can. */
export function riskProblem(entry) {
    const a = entry.kind === "risk" ? "a risk" : entry.kind === "bug" ? "a bug" : "an issue";
    if (!statusesFor(entry.kind).includes(entry.status))
        return `${a} cannot be "${entry.status}"`;
    if (entry.kind !== "risk" && entry.likelihood)
        return `${a} has happened: it has no likelihood`;
    if (entry.kind !== "issue" && entry.fromRisk)
        return "only an issue names the risk it came from";
    if (entry.kind !== "risk" && (entry.inherent || entry.residual))
        return `only a risk is assessed for likelihood and impact; ${a} has happened`;
    if (entry.kind !== "bug" && entry.severity)
        return "severity is a bug's";
    return undefined;
}
/** A patch laid over an entry: `null` drops an optional field, anything absent stays. */
export function applyRiskPatch(entry, patch) {
    const next = { ...entry };
    for (const [key, value] of Object.entries(patch)) {
        if (value === undefined)
            continue;
        if (value === null)
            delete next[key];
        else
            next[key] = value;
    }
    return next;
}
/** How exposed something is: the open entries among these, counted by rating. */
export function riskExposure(entries) {
    const open = { critical: 0, high: 0, medium: 0, low: 0 };
    let risks = 0;
    let issues = 0;
    let bugs = 0;
    for (const e of entries) {
        if (!isOpenEntry(e))
            continue;
        open[riskRating(e)]++;
        if (e.kind === "risk")
            risks++;
        else if (e.kind === "bug")
            bugs++;
        else
            issues++;
    }
    const worst = RISK_RATINGS.find((r) => open[r] > 0);
    return { open, risks, issues, ...(bugs ? { bugs } : {}), ...(worst ? { worst } : {}) };
}
/** The entries of one repository ("" = the project's own), or of every one when `rootId` is undefined. */
export function entriesOf(entries, rootId) {
    return rootId === undefined ? [...entries] : entries.filter((e) => e.rootId === rootId);
}
/** The entries that threaten a roadmap item (a repository's row, or the project's with `rootId: ""`). */
export function entriesForRoadmapRow(entries, rootId, rowId) {
    return entries.filter((e) => e.roadmap.some((l) => l.rootId === rootId && l.id === rowId));
}
/** The entries that threaten, or came from, a task (by its uid). */
export function entriesForTask(entries, uid) {
    return entries.filter((e) => e.tasks.some((l) => l.uid === uid));
}
/** Most pressing first: open before settled, then by rating, then the oldest. */
export function byUrgency(a, b) {
    const open = Number(isOpenEntry(b)) - Number(isOpenEntry(a));
    if (open)
        return open;
    const rating = RISK_RATINGS.indexOf(riskRating(a)) - RISK_RATINGS.indexOf(riskRating(b));
    return rating || a.raisedAt.localeCompare(b.raisedAt);
}
// ── Activity: what a home writes to the project's log for an accepted write ──
export const RISK_ACTIVITY_KINDS = ["risk.raised", "risk.changed", "risk.status", "risk.removed"];
const noun = (e) => e.kind;
/** The lines for one write: raised, removed, a status that moved, or what else changed. Nothing when nothing did. */
export function riskActivity(before, after) {
    if (!before && !after)
        return [];
    if (!before && after)
        return [{ kind: "risk.raised", rootId: after.rootId, target: after.id, text: `raised the ${noun(after)} “${after.title}” (${RISK_RATING_TEXT[riskRating(after)].toLowerCase()})`, detail: { uid: after.uid, kind: after.kind, rating: riskRating(after) } }];
    if (before && !after)
        return [{ kind: "risk.removed", rootId: before.rootId, target: before.id, text: `removed the ${noun(before)} “${before.title}”`, detail: { uid: before.uid } }];
    const b = before;
    const a = after;
    const out = [];
    if (b.status !== a.status)
        out.push({ kind: "risk.status", rootId: a.rootId, target: a.id, text: `${noun(a)} “${a.title}”: ${RISK_STATUS_TEXT[b.status].toLowerCase()} → ${RISK_STATUS_TEXT[a.status].toLowerCase()}`, detail: { uid: a.uid, from: b.status, to: a.status } });
    const changed = ["title", "description", "likelihood", "impact", "owner", "response", "due", "category", "disposition", "confidentiality", "identifiedOn", "reviewDue", "reviewedAt", "severity", "repro", "foundIn", "pullRequest"].filter((k) => b[k] !== a[k]);
    for (const k of ["inherent", "residual", "controls", "treatments", "tags", "affects"])
        if (JSON.stringify(b[k] ?? null) !== JSON.stringify(a[k] ?? null))
            changed.push(k);
    if (JSON.stringify(b.roadmap) !== JSON.stringify(a.roadmap) || JSON.stringify(b.tasks) !== JSON.stringify(a.tasks))
        changed.push("what it threatens");
    if (changed.length)
        out.push({ kind: "risk.changed", rootId: a.rootId, target: a.id, text: `changed the ${noun(a)} “${a.title}”: ${changed.join(", ")}`, detail: { uid: a.uid, fields: changed } });
    return out;
}
//# sourceMappingURL=projectRisks.js.map